import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef, IServerSideGetRowsRequest } from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgTypePillCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-type-pill-cell-renderer/ag-type-pill-cell-renderer.component';
import { AgMetricCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-metric-cell-renderer/ag-metric-cell-renderer.component';
import {
  buildAuditTrailColumnGroup,
  buildOwnerColumnDef,
} from 'src/app/ag-grid/utils/audit-trail-columns.utils';
import { buildNomenclatureColumnDef } from 'src/app/ag-grid/utils/nomenclature-column.utils';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { redirectToObject } from 'src/app/shared/utils/object-utils';
import { DataGridHostBase } from 'src/app/ag-grid/components/data-grid/data-grid-host-base';

@Component({
  selector: 'app-feeds',
  templateUrl: './feeds.component.html',
  standalone: false,
})
export class FeedsComponent extends DataGridHostBase {
  private readonly nomenclatureService = inject(NomenclatureService);
  private readonly userApi = inject(UserApiService);
  private readonly editService = inject(ObjectEditorService);
  private readonly router = inject(Router);

  readonly viewId = ViewId.Feeds;
  readonly title = this.translateService.instant('grid.title.feeds');
  readonly icon: IconProp = ['fas', 'rss'];

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.userApi.getUserFeedsAgGrid(request);

  readonly columnDefs: ColDef[] = this.buildColumnDefs();

  private buildColumnDefs(): ColDef[] {
    return [
      {
        headerName: this.translateService.instant('grid.column.name'),
        field: 'title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          objectId: 'object_id',
          objectClassId: 'object_class_id',
          objectTypeId: 'object_type_id',
          lifecycleStatus: 'lifecycle_status',
          onClick: (data) => {
            redirectToObject(data, this.router, true);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        ...buildNomenclatureColumnDef(
          this.translateService,
          this.nomenclatureService,
          {
            headerNameKey: 'grid.column.type',
            fieldKey: 'object_type.id',
            fieldValue: 'object_type.title',
            nomenclatureName: 'query_type',
            otherFilterParams: {
              defaultToNothingSelected: true,
            },
          },
        ),
        cellRenderer: AgTypePillCellRendererComponent,
      },
      {
        headerName: this.translateService.instant('grid.column.description'),
        field: 'description',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this.translateService.instant(
          'grid.column.dimensions-count',
        ),
        field: 'dimensions_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant('grid.column.alerts-count'),
        field: 'alerts_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedules-count',
        ),
        field: 'export_schedules_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant(
          'grid.column.algorithms-count',
        ),
        field: 'algorithms_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
        cellRenderer: AgMetricCellRendererComponent,
      },
      {
        headerName: this.translateService.instant('grid.column.workspace'),
        field: 'workspace.title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'workspace',
          titleKey: 'title',
          icon: ['fas', 'folder'],
          // Only refs the dispatcher can open are links.
          clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
          onClick: (ref) => this.editService.openDataObjectRef(ref),
        },
        cellStyle: { overflow: 'visible' },
      },
      buildOwnerColumnDef(this.translateService, this.openUser),
      buildAuditTrailColumnGroup(this.translateService, {
        area: 'analytics',
        onUserClick: this.openUser,
      }),
    ];
  }
}
